import { useVersions, useStartVersionScan } from '../../data/versions'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Button } from '../../ui/Button'
import { EmptyState, ErrorState, Skeleton } from '../../ui/Feedback'
import { PageHeader } from '../shared/PageHeader'
import { VersionSummary } from './VersionSummary'
import { VersionTable } from './VersionTable'

const versionsQueryClient = new QueryClient({ defaultOptions: { queries: { retry: false } } })

/** Operator-only versions report: summary, component table, and scan trigger. */
function VersionsReport() {
  const query = useVersions()
  const scan = useStartVersionScan()
  const components = query.data?.components ?? []
  const hasReport = components.length > 0
  // A scan invalidates and refetches, and its mutation stays pending until that
  // refetch lands. Show the loading state for the whole of that, but only while
  // there is nothing to show: an existing report stays on screen while it
  // refreshes rather than collapsing back to a skeleton.
  const loading = query.isLoading || (scan.isPending && !hasReport)
  return <>
    <PageHeader eyebrow="Operations" title="Operator versions" description="See the software versions reported by the operator workspace." />
    <div className="sign-sheet-actions"><Button variant="primary" loading={scan.isPending} disabled={scan.isPending} onClick={() => { scan.mutate() }}>Scan now</Button></div>
    {loading && <div role="status" aria-label="Loading versions"><Skeleton /><Skeleton /></div>}
    {!loading && query.error && <ErrorState body={query.error instanceof Error ? query.error.message : 'Version report unavailable'} onRetry={() => void query.refetch()} retrying={query.isFetching} />}
    {!loading && !query.error && !hasReport && <section role="region" aria-label="Versions unavailable" className="card"><EmptyState title="No version report yet" body="No scan has recorded a component version yet. Run a scan to collect one." /></section>}
    {hasReport && <><VersionSummary components={components} manifestAgeSeconds={query.data?.manifest_age_seconds ?? null} /><VersionTable rows={components} /></>}
  </>
}

// OperatorPage is also embeddable in isolated previews without the app shell's
// provider. Keep this route self-contained while the app provider still owns
// the rest of the workspace queries.
export function VersionsPage() {
  return <QueryClientProvider client={versionsQueryClient}><VersionsReport /></QueryClientProvider>
}
